import { Fragment } from "react";
import { motion } from "framer-motion";
import { useContent } from "@/context/ContentContext";
import { Reveal } from "@/components/Reveal";
import PageHeader from "@/components/PageHeader";
import ImageReveal from "@/components/motion/ImageReveal";
import Button from "@/components/Button";
import { BrandIcon } from "@/components/BrandIcon";
import YearSection from "@/components/YearSection";
import JoinCta from "@/components/JoinCta";
import EventsSection from "@/components/EventsSection";

export default function Programs() {
  const c = useContent();
  const items = c.pairs("offerings", "items");

  return (
    <div data-testid="programs-page">
      <PageHeader testid="programs-header" kicker={c.get("offerings", "kicker")} title={c.get("offerings", "title")} />

      {/* Index strip */}
      <section className="border-b border-line bg-bone" data-testid="programs-index">
        <div className="mx-auto flex max-w-[1200px] flex-wrap gap-x-8 gap-y-3 px-5 py-5">
          {items.map(([title], i) => (
            <a
              key={i}
              href={`#programme-${i}`}
              onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById(`programme-${i}`);
                if (el)
                  window.__lenis
                    ? window.__lenis.scrollTo(el, { offset: -100 })
                    : el.scrollIntoView({ behavior: "smooth" });
              }}
              className="link-line flex items-center gap-2 font-display text-sm font-medium text-ink hover:text-pine"
              data-testid={`programs-index-${i}`}
            >
              <span className="font-display font-semibold text-goldtext">0{i + 1}</span> {title}
            </a>
          ))}
        </div>
      </section>

      <section className="border-b border-line" data-testid="programs-list">
        {items.map(([title, desc, img, icon], i) => (
          <Fragment key={i}>
            <article
              id={`programme-${i}`}
              className={`group grid border-b border-line last:border-b-0 md:min-h-[80vh] md:grid-cols-12 ${i % 2 ? "bg-bone" : "bg-surface"}`}
              data-testid={`program-item-${i}`}
            >
              <div className={`relative md:col-span-6 ${i % 2 ? "md:order-2" : ""}`}>
                <ImageReveal
                  src={img || "/images/texture.jpg"}
                  alt={title}
                  direction={i % 2 ? "right" : "left"}
                  parallax={10}
                  className="aspect-[16/11] md:sticky md:top-24 md:m-5 md:aspect-auto md:h-[calc(100vh-7.5rem)] md:rounded-[10px]"
                />
              </div>
              <div className="flex flex-col justify-center px-6 py-16 md:col-span-6 md:px-16 md:py-24 lg:px-24">
                <Reveal className="flex items-center gap-5">
                  <span className="flex h-14 w-14 items-center justify-center rounded-md bg-bone text-pine transition-colors duration-500 group-hover:bg-pine group-hover:text-white">
                    <BrandIcon name={icon} className="h-6 w-6" />
                  </span>
                  <span className="font-display text-base font-semibold text-goldtext">
                    0{i + 1} / 0{items.length}
                  </span>
                </Reveal>
                <Reveal delay={0.1}>
                  <h2 className="mt-8 font-display text-3xl font-semibold leading-[1.15] text-ink md:text-[2.6rem]">
                    {title}
                  </h2>
                </Reveal>
                <Reveal delay={0.2}>
                  <p className="mt-6 max-w-[62ch] text-lg leading-relaxed text-body">{desc}</p>
                </Reveal>
                <Reveal delay={0.3} className="mt-10">
                  <Button to="/membership" variant="outline" arrow="up" data-testid={`program-cta-${i}`}>
                    Join to take part
                  </Button>
                </Reveal>
                <motion.div
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.2, ease: [0.76, 0, 0.24, 1] }}
                  className="mt-16 h-px origin-left bg-line"
                />
              </div>
            </article>
          </Fragment>
        ))}
      </section>

      <YearSection />
      <div id="events">
        <EventsSection tint />
      </div>
      <JoinCta />
    </div>
  );
}
